import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import SubPageHero from '@/components/SubPageHero';
import ConfirmModal from '@/components/ui/ConfirmModal';
import s from './Inquiry.module.scss';

const CAT: Record<string, string> = { PRODUCT: '제품·솔루션', INSTALL_AS: '설치·A/S', QUOTE: '견적·도입', DEMO: '체험·데모', ETC: '기타' };

interface Row { id: number; category: string; title: string; status: string; created_at: string; }
interface Detail { id: number; category: string; title: string; content: string; status: string; answer?: string | null; answered_at?: string | null; created_at: string; attach_name?: string | null; }

/** [2026-09-10] 내 문의 — 목록(페이징) + 클릭 시 상세(내용·답변). 로그인 필수. */
function MyInquiries({ embedded }: { embedded?: boolean }) {
	const auth = useAuth();
	const nav = useNavigate();
	const [rows, setRows] = useState<Row[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [open, setOpen] = useState<Detail | null>(null);
	const [delId, setDelId] = useState<number | null>(null);   // [2026-09-12] 삭제 확인 = 네이티브 confirm 대신 모달
	const [errMsg, setErrMsg] = useState<string | null>(null); // 삭제 실패 = 네이티브 alert 대신 모달
	const size = 10;

	const load = useCallback(async () => {
		const r = await authApi.inquiryMine(page, size);
		if (r.success) { setRows((r.items as Row[]) || []); setTotal(Number(r.total) || 0); }
	}, [page]);

	useEffect(() => { if (auth.isAuthed) load(); }, [load, auth.isAuthed]);

	async function openDetail(id: number) {
		const r = await authApi.inquiryDetail(id);
		if (r.success) setOpen(r.inquiry as Detail);
	}
	async function doDelete() {
		if (delId == null) return;
		const r = await authApi.inquiryDelete(delId);
		setDelId(null);
		if (!r.success) { setErrMsg(r.message || '삭제에 실패했습니다.'); return; }
		setOpen(null); load();
	}

	const totalPages = Math.max(1, Math.ceil(total / size));

	return (
		<>
			{!embedded && <SubPageHero title="내 문의" subtitle="문의 내역과 답변을 확인하세요"
				image="/images/auth/hero.jpg" crumbs={[{ label: 'HOME' }, { label: '고객지원' }, { label: '내 문의' }]} />}
			<ConfirmModal open={delId != null} danger title="문의 삭제" confirmText="삭제"
				message="이 문의를 삭제하시겠습니까? 되돌릴 수 없습니다." onConfirm={doDelete} onCancel={() => setDelId(null)} />
			<ConfirmModal open={errMsg != null} alertMode title="알림" message={errMsg || ''}
				onConfirm={() => setErrMsg(null)} onCancel={() => setErrMsg(null)} />
			<div className={s.wrap}>
				<div className={s.inner}>
					{auth.ready && !auth.isAuthed ? (
						<div className={s.notice}>
							<p>로그인 후 이용하실 수 있습니다.</p>
							<button className={s.primary} onClick={() => nav('/login')}>로그인하러 가기</button>
						</div>
					) : open ? (
						<div className={s.notice} style={{ textAlign: 'left', padding: 28 }}>
							<p className={s.sectionLabel}>{CAT[open.category] || open.category} · {open.status === 'ANSWERED' ? '답변완료' : '답변대기'}</p>
							<h2 className={s.h2}>{open.title}</h2>
							<p style={{ color: '#495562', whiteSpace: 'pre-wrap', lineHeight: 1.7, margin: '12px 0 16px' }}>{open.content}</p>
							{open.attach_name && (
								<button className={s.ghost} style={{ marginBottom: 20, display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 16px', width: 'auto' }}
									onClick={() => authApi.downloadInquiryAttachment(open.id, open.attach_name!)}>📎 {open.attach_name}</button>
							)}
							{open.answer ? (
								<div style={{ background: '#f0faff', border: '1px solid #cfeefb', borderRadius: 10, padding: 18 }}>
									<p className={s.sectionLabel} style={{ marginBottom: 8 }}>EVS 답변</p>
									<p style={{ color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.7, margin: 0 }}>{open.answer}</p>
								</div>
							) : <p style={{ color: '#8299ab' }}>아직 답변이 등록되지 않았습니다.</p>}
							<div className={s.actions}>
								<button className={s.ghost} onClick={() => setOpen(null)}>목록으로</button>
								{open.status !== 'ANSWERED' && <button className={s.ghost} style={{ color: '#c0292e' }} onClick={() => setDelId(open.id)}>삭제</button>}
							</div>
						</div>
					) : (
						<>
							<p className={s.sectionLabel}>MY INQUIRIES</p>
							<h2 className={s.h2}>내 문의 내역</h2>
							<p className={s.lead}>총 {total}건 · 제목을 클릭하면 상세·답변을 볼 수 있습니다.</p>
							<div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 12, overflow: 'hidden' }}>
								{rows.length === 0 ? (
									<div style={{ padding: 40, textAlign: 'center', color: '#8299ab' }}>문의 내역이 없습니다.</div>
								) : rows.map((r) => (
									<div key={r.id} onClick={() => openDetail(r.id)}
										style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid #eef2f6', cursor: 'pointer' }}>
										<span style={{ fontSize: 11, fontWeight: 700, color: '#0b84b3', background: '#e7f6fd', borderRadius: 20, padding: '2px 9px', flex: '0 0 auto' }}>{CAT[r.category] || r.category}</span>
										<span style={{ flex: 1, fontWeight: 600, color: '#222833', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
										<span style={{ fontSize: 12, fontWeight: 700, color: r.status === 'ANSWERED' ? '#1f8a4c' : '#96690a', flex: '0 0 auto' }}>{r.status === 'ANSWERED' ? '답변완료' : '답변대기'}</span>
										<span style={{ fontSize: 12.5, color: '#8299ab', flex: '0 0 auto' }}>{String(r.created_at).slice(0, 10)}</span>
									</div>
								))}
							</div>
							<div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 18, alignItems: 'center', color: '#8299ab', fontSize: 13.5 }}>
								<button className={s.ghost} style={{ height: 38, padding: '0 14px' }} disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>이전</button>
								<span>{page + 1} / {totalPages}</span>
								<button className={s.ghost} style={{ height: 38, padding: '0 14px' }} disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>다음</button>
							</div>
							<div className={s.actions}>
								<button className={s.primary} onClick={() => nav('/support/inquiry')}>새 문의하기</button>
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
}

export default observer(MyInquiries);
